import type { User } from "lib/apollo/types";
import { useUser } from "lib/user";
import { useRouter } from "lib/router";
import { useEffect, useRef } from "react";
import {
  addGTMEvent,
  canSendPageView,
  drainPageEventQueue,
  setPrevPage,
  trackBasket,
  useIsGTMEnabled
} from "utils/gtm";
import { resetPageImpressions } from "utils/gtm/impressions";
import { DataLayerEvent } from "utils/gtm/types";
import { useCurrencyCode } from "./useCurrencyCode";
import { getRegion } from "@shared/utils.locale";

const TRACKING_URL_BASKET = "/basket";
const TRACKING_URL_CHECKOUT = "/checkout";
const TRACKING_URL_CHECKOUT_SUCCESS = "/checkout/success";
const TRACKING_URL_REGISTER = "/register";
const TRACKING_URL_GUEST_CHECKOUT = "/guest-details";

export const useGTMEvents = (is404 = false): void => {
  const { asPath, pathname, query } = useRouter();
  const region = getRegion();
  const isGTMEnabled = useIsGTMEnabled();
  const user = useUser();
  const currencyCode = useCurrencyCode();

  const referrerRef = useRef("");
  const userRef = useRef<User>(user);
  const hasMembershipRef = useRef<boolean>(false);

  // Skip the initial page view event
  // See https://beautypie.atlassian.net/browse/ROC-17?focusedCommentId=12991
  const firstPageRef = useRef<boolean>(true);

  useEffect(() => {
    userRef.current = user;
    hasMembershipRef.current = user.hasMembership;
  }, [user]);

  // add page view event
  useEffect(() => {
    // Skip page view if query string has "refresh" or if GTM is disabled i.e. stress testing
    // URLs like basket?refresh=some-value won't be tracked
    const { refresh } = query;
    if (refresh || !isGTMEnabled) return;

    const currentUser = userRef.current;
    const basket = currentUser?.basket || [];
    const isLoggedIn = currentUser?.isLoggedIn;

    // reset page impressions on path change
    resetPageImpressions(currencyCode);

    const isPageView = canSendPageView(asPath, pathname, false);

    if (firstPageRef.current === false) {
      // fire event on each path change
      if (isPageView) {
        // do not send a page view in the initial load as one is already sent by GTM automatically
        addGTMEvent({
          name: DataLayerEvent.PageView,
          url: asPath,
          locale: region,
          pathname,
          referrer: referrerRef.current || "",
          user: userRef.current,
          is404
        });
      }
    }

    if (
      basket &&
      (isPageView || firstPageRef.current === true) &&
      [
        TRACKING_URL_BASKET,
        TRACKING_URL_CHECKOUT,
        TRACKING_URL_REGISTER,
        TRACKING_URL_GUEST_CHECKOUT
      ].some((path) => pathname.startsWith(path))
    ) {
      let step = isLoggedIn ? 3 : 1;

      if (pathname.startsWith(TRACKING_URL_CHECKOUT_SUCCESS)) {
        return;
      }

      // Set checkout step
      if (pathname.startsWith(TRACKING_URL_CHECKOUT)) {
        step = 4;
      }

      // Set register | guest-checkout step
      if (
        pathname.startsWith(TRACKING_URL_REGISTER) ||
        pathname.startsWith(TRACKING_URL_GUEST_CHECKOUT)
      ) {
        step = 2;
      }

      trackBasket(basket ?? [], step, currencyCode);
    }

    if (firstPageRef.current === true) {
      setPrevPage(asPath);
      firstPageRef.current = false;
      drainPageEventQueue(asPath);
    }

    referrerRef.current = asPath;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- there should not be any other dependencies here other than asPath
  }, [asPath]);
};
